import { Chart } from '@frontend/chart/types';

import { normalizeChartDateLabel } from './to-weekday-week-heatmap-series';

export const HOUR_DAY_HEATMAP_HOURS = 24;
/** Two weeks of daily rows (hours along X). */
export const HOUR_DAY_HEATMAP_DAYS = 14;

export interface HourDayHeatmapCell {
    x: string;
    y: number;
    meta: {
        date: string;
        hour: number;
    };
}

export interface HourDayHeatmapSeries {
    name: string;
    data: HourDayHeatmapCell[];
}

export interface HourDayHeatmapModel {
    series: HourDayHeatmapSeries[];
    /** Hour labels for the X axis (columns). */
    hourLabels: string[];
    maxValue: number;
}

const parseDateTimeLabel = (label: unknown): { date: string; hour: number } | null => {
    if (label === null || label === undefined) {
        return null;
    }

    if (typeof label === 'number' && Number.isFinite(label)) {
        const milliseconds = label < 1e12 ? label * 1000 : label;
        const date = new Date(milliseconds);
        if (Number.isNaN(date.getTime())) {
            return null;
        }
        return {
            date: date.toISOString().slice(0, 10),
            hour: date.getUTCHours()
        };
    }

    const text = String(label).trim();
    if (!text) {
        return null;
    }

    if (/^\d+$/.test(text)) {
        return parseDateTimeLabel(Number(text));
    }

    const isoMatch = /^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}))?/.exec(text);
    if (isoMatch) {
        return {
            date: isoMatch[1],
            hour: isoMatch[2] ? Number(isoMatch[2]) : 0
        };
    }

    const parsed = new Date(text);
    if (!Number.isNaN(parsed.getTime())) {
        return {
            date: parsed.toISOString().slice(0, 10),
            hour: parsed.getUTCHours()
        };
    }

    const dateOnly = normalizeChartDateLabel(text);
    if (!dateOnly) {
        return null;
    }

    return { date: dateOnly, hour: 0 };
};

const toUtcMidnight = (date: Date): Date => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const buildDayLabels = (rangeStart: Date, dayCount: number): string[] => {
    const alignedStart = toUtcMidnight(rangeStart);

    return Array.from({ length: dayCount }, (_, dayIndex) => {
        const day = new Date(alignedStart);
        day.setUTCDate(alignedStart.getUTCDate() + dayIndex);
        return day.toISOString().slice(0, 10);
    });
};

export const deriveHourDayHeatmapRangeStart = (chart: Chart): Date | null => {
    const stamps = (chart.series ?? [])
        .flatMap((series) => (series.data ?? []).map((point) => parseDateTimeLabel(point.label)))
        .filter((entry): entry is { date: string; hour: number } => !!entry)
        .map((entry) => entry.date)
        .sort();

    if (stamps.length === 0) {
        return null;
    }

    const first = new Date(`${stamps[0]}T00:00:00.000Z`);
    return Number.isNaN(first.getTime()) ? null : first;
};

export const toHourDayHeatmapModel = (
    chart: Chart,
    rangeStart: Date,
    dayCount = HOUR_DAY_HEATMAP_DAYS,
    hourLabels: string[] = []
): HourDayHeatmapModel | null => {
    if (hourLabels.length !== HOUR_DAY_HEATMAP_HOURS) {
        return null;
    }

    const alignedRangeStart = toUtcMidnight(rangeStart);
    const dayLabels = buildDayLabels(alignedRangeStart, dayCount);
    const dayIndexByLabel = new Map(dayLabels.map((label, index) => [label, index]));
    // grid[dayIndex][hour] — days as rows (Y), hours as columns (X)
    const grid = Array.from({ length: dayCount }, () => Array(HOUR_DAY_HEATMAP_HOURS).fill(0));

    for (const series of chart.series ?? []) {
        for (const point of series.data ?? []) {
            const stamp = parseDateTimeLabel(point.label);
            if (!stamp) {
                continue;
            }

            const dayIndex = dayIndexByLabel.get(stamp.date);
            if (dayIndex === undefined || stamp.hour < 0 || stamp.hour >= HOUR_DAY_HEATMAP_HOURS) {
                continue;
            }

            grid[dayIndex][stamp.hour] += point.value;
        }
    }

    let maxValue = 0;
    const series = dayLabels.map((dayLabel, dayIndex) => ({
        name: dayLabel,
        data: hourLabels.map((hourLabel, hour) => {
            const y = grid[dayIndex][hour];
            maxValue = Math.max(maxValue, y);
            return {
                x: hourLabel,
                y,
                meta: { date: dayLabel, hour }
            };
        })
    }));

    return { series, hourLabels, maxValue };
};
